<template>
    <!-- App-level floating report-progress bar. Reads progress from the Vuex
         reportProgress module so it survives route navigation while a report
         generates in the background. Hidden while the report dialog is open
         (the dialog shows its own inline progress). -->
    <Teleport to="body">
        <Transition name="report-fab-fade">
            <v-card v-if="(isGenerating || complete) && !dialogOpen"
                    class="report-progress-fab"
                    elevation="8"
                    @click="reopen">
                <div class="d-flex align-center mb-1" style="gap: 8px;">
                    <v-progress-circular v-if="isGenerating"
                                         indeterminate
                                         size="18"
                                         width="2"
                                         color="primary"></v-progress-circular>
                    <v-icon v-else size="18" color="success">mdi-check-circle</v-icon>
                    <span class="text-caption font-weight-medium text-truncate" style="flex: 1; min-width: 0;">
                        {{ complete ? t('reportReadyTitle') : (text || t('generateReport')) }}
                    </span>
                    <span class="text-caption">{{ Math.round(progress) }}%</span>
                </div>
                <v-progress-linear :model-value="progress"
                                   :indeterminate="isGenerating && progress <= 0"
                                   :color="complete ? 'success' : 'primary'"
                                   height="6"
                                   rounded></v-progress-linear>
            </v-card>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { computed } from 'vue';
    import { useStore } from 'vuex';
    import { useI18n } from 'vue-i18n';

    const store = useStore();
    const { t } = useI18n();

    const isGenerating = computed(() => store.state.reportProgress.isGenerating);
    const complete = computed(() => store.state.reportProgress.complete);
    const progress = computed(() => store.state.reportProgress.progress);
    const text = computed(() => store.state.reportProgress.text);
    const dialogOpen = computed(() => store.state.reportProgress.dialogOpen);

    // Ask a mounted report dialog (if any, on the current page) to re-open.
    const reopen = () => {
        store.commit('reportProgress/requestOpen');
    };
</script>

<style scoped>
    .report-progress-fab {
        position: fixed;
        bottom: 16px;
        left: 16px;
        width: 320px;
        max-width: calc(100vw - 32px);
        padding: 12px 16px;
        z-index: 2400;
        cursor: pointer;
    }

    /* Fade (with a slight slide) when the mini bar appears / dismisses. */
    .report-fab-fade-enter-active,
    .report-fab-fade-leave-active {
        transition: opacity 0.4s ease, transform 0.4s ease;
    }

    .report-fab-fade-enter-from,
    .report-fab-fade-leave-to {
        opacity: 0;
        transform: translateY(8px);
    }
</style>
